import { useState } from "react";
import { Calculator, UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { useContent, awardPoints } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ACTIVITY = { "Sedentary": 1.2, "Light": 1.375, "Moderate": 1.55, "Very active": 1.725 };

export default function Nutrition() {
  const { data: recipes } = useContent("recipes");
  const { data: mealplans } = useContent("mealplans");
  const [f, setF] = useState({ gender: "Male", age: 28, height: 172, weight: 75, activity: "Moderate", goal: "Maintain" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const w = Number(f.weight), h = Number(f.height), a = Number(f.age);
  const bmi = w && h ? (w / ((h / 100) ** 2)) : 0;
  const bmr = f.gender === "Male" ? 10 * w + 6.25 * h - 5 * a + 5 : 10 * w + 6.25 * h - 5 * a - 161;
  const tdee = bmr * (ACTIVITY[f.activity] || 1.55);
  const goalCal = f.goal === "Fat Loss" ? tdee - 400 : f.goal === "Muscle Gain" ? tdee + 300 : tdee;
  const protein = Math.round(w * 1.8);
  const fat = Math.round((goalCal * 0.25) / 9);
  const carbs = Math.round((goalCal - protein * 4 - fat * 9) / 4);
  const bmiCat = bmi < 18.5 ? "Underweight" : bmi < 25 ? "Healthy" : bmi < 30 ? "Overweight" : "Obese";

  const Field = ({ label, k, type = "number" }) => (
    <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{label}</Label>
      <Input type={type} data-testid={`nut-${k}`} value={f[k]} onChange={(e) => set(k)(e.target.value)} className="mt-2 h-11" /></div>
  );

  return (
    <div data-testid="nutrition-page" className="px-4 md:px-8 pb-16 pt-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display font-900 uppercase text-4xl md:text-5xl tracking-tight">Nutrition</h1>
          <p className="text-muted-foreground mt-1">Calculators, meal plans and recipes to fuel your goals.</p>
        </div>
        <button data-testid="log-meal-btn" onClick={async () => { const p = await awardPoints("log_meal"); toast.success(p ? `Meal logged! +${p} pts` : "Meal already logged today"); }}
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-full text-xs uppercase tracking-[0.12em] font-bold hover:scale-[1.02] transition-transform"><UtensilsCrossed size={16} /> Log a Meal</button>
      </div>

      <Tabs defaultValue="calc" className="mt-6">
        <TabsList>
          <TabsTrigger value="calc" data-testid="tab-calculators">Calculators</TabsTrigger>
          <TabsTrigger value="plans" data-testid="tab-mealplans">Meal Plans</TabsTrigger>
          <TabsTrigger value="recipes" data-testid="tab-recipes">Recipes</TabsTrigger>
        </TabsList>

        <TabsContent value="calc" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="border border-border rounded-xl bg-card p-6" data-testid="calc-form">
              <h3 className="font-display font-700 uppercase text-2xl tracking-tight mb-4 flex items-center gap-2"><Calculator size={20} className="text-primary" /> Your Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Gender</Label>
                  <Select value={f.gender} onValueChange={set("gender")}><SelectTrigger className="mt-2 h-11" data-testid="nut-gender"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem></SelectContent></Select></div>
                <Field label="Age" k="age" />
                <Field label="Height (cm)" k="height" />
                <Field label="Weight (kg)" k="weight" />
                <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Activity</Label>
                  <Select value={f.activity} onValueChange={set("activity")}><SelectTrigger className="mt-2 h-11" data-testid="nut-activity"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(ACTIVITY).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Goal</Label>
                  <Select value={f.goal} onValueChange={set("goal")}><SelectTrigger className="mt-2 h-11" data-testid="nut-goal"><SelectValue /></SelectTrigger><SelectContent>{["Fat Loss", "Maintain", "Muscle Gain"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 content-start" data-testid="calc-results">
              {[{ l: "BMI", v: bmi.toFixed(1), s: bmiCat }, { l: "BMR", v: Math.round(bmr), s: "kcal at rest" },
                { l: "Daily Calories", v: Math.round(goalCal), s: `for ${f.goal}` }, { l: "Protein", v: `${protein}g`, s: "per day" },
                { l: "Carbs", v: `${carbs}g`, s: "per day" }, { l: "Fats", v: `${fat}g`, s: "per day" }].map((r) => (
                <div key={r.l} className="border border-border rounded-xl bg-card p-5">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{r.l}</div>
                  <div className="font-display font-900 text-4xl text-primary leading-none mt-1">{r.v}</div>
                  <div className="text-xs text-muted-foreground mt-1">{r.s}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="plans" className="mt-6">
          <div className="grid gap-5 md:grid-cols-3">
            {mealplans.map((m) => (
              <div key={m.id} className="border border-border rounded-xl overflow-hidden bg-card" data-testid="mealplan-card">
                <div className="aspect-[16/10] overflow-hidden"><img src={m.image} alt={m.title} className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <span className="text-[10px] uppercase tracking-wide text-primary font-bold">{m.goal} · {m.calories}</span>
                  <h3 className="font-display font-700 uppercase text-2xl tracking-tight mt-1">{m.title}</h3>
                  <p className="text-muted-foreground text-sm mt-1">{m.description}</p>
                  <ul className="mt-3 space-y-1.5 text-sm">{(m.meals || []).map((x, i) => <li key={i} className="text-muted-foreground">• {x}</li>)}</ul>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="recipes" className="mt-6">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {recipes.map((r) => (
              <div key={r.id} className="border border-border rounded-xl overflow-hidden bg-card" data-testid="recipe-card">
                <div className="aspect-[16/10] overflow-hidden"><img src={r.image} alt={r.title} className="w-full h-full object-cover" /></div>
                <div className="p-5">
                  <span className="text-[10px] uppercase tracking-wide text-primary font-bold">{r.category} · {r.time}</span>
                  <h3 className="font-display font-700 uppercase text-xl tracking-tight mt-1">{r.title}</h3>
                  <p className="text-muted-foreground text-sm mt-1">{r.description}</p>
                  <div className="flex gap-4 text-xs text-muted-foreground mt-3"><span>{r.calories} kcal</span><span>{r.protein_g}g protein</span></div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
